import React, { useEffect, useState } from 'react';
import {
  StyleSheet, Image, Text, View,
  Dimensions,
  Pressable,
} from 'react-native';
import { DrawerContentScrollView } from '@react-navigation/drawer';
import AsyncStorage from '@react-native-async-storage/async-storage';
import Modal from 'react-native-modal';
import { useNavigation, useRoute } from '@react-navigation/native';
import { useSelector } from 'react-redux';
import Colors from '../constants/Colors';
import ExpandableDrawer from './ExpandableDrawer';
import ProfileIcon from './24pxIcons/ProfileIcon';
import SupportIcon from './24pxIcons/SupportIcon';
import Style from '../constants/Style';
import CloseModalIcon from './CloseModalIcon';
import SupportScreen from '../screens/SupportScreen';
import DoubleRightIcon from './24pxIcons/DoubleRightIcon';
import { logEvent } from './LogEvent';
import PressableStyled from './PressableStyled';

interface Props {
  state: any;
  navigation: any;
  descriptors: any;
  toggleDarkMode: any;
  collapsed: boolean;
  setCollapsed: any;
}

const defaultLogoUri: string = 'https://s3.eu-west-1.amazonaws.com/insights.hereworks.tech/clientLogos/hereworks-logo.png';

const CustomDrawer = (props: Props) => {
  const window = Dimensions.get('window');
  const [clientLogo, setClientLogo] = useState(defaultLogoUri);
  const [clientId, setClientId] = useState('hereworks');
  const [supportOpen, setSupportOpen] = useState<boolean>(false);
  const {
    firstName, lastName, isAdmin, permissions, enabledFeatures,
  } = useSelector((state:
    { user: { firstName, lastName, isAdmin, permissions,
      enabledFeatures: { chatbot: boolean } } }) => state.user);

  const route = useRoute();

  useEffect(() => {
    AsyncStorage.getItem('clientLogo').then((logo: string) => {
      if (logo?.length) setClientLogo(logo);
      else {
        AsyncStorage.getItem('clientName').then((name: string) => {
          if (name?.toLowerCase().includes('mastercard')) {
            setClientLogo(
              'https://s3.eu-west-1.amazonaws.com/insights.hereworks.tech/clientLogos/mastercard-hereworks.png',
            );
          } else if (name?.toLowerCase().includes('an post')) {
            setClientLogo(
              'https://s3.eu-west-1.amazonaws.com/insights.hereworks.tech/clientLogos/anpost-hereworks.png',
            );
          }
        });
      }
    });
    AsyncStorage.getItem('clientName').then((n) => {
      setClientId(n);
    });
  }, []);

  useEffect(() => {
    setSupportOpen(false);
  }, [route]);

  return (
    <View style={{ flex: 1, backgroundColor: Colors.light.hereworksBackground }}>
      <View style={styles.drawer}>
        <Image
          source={{
            uri: clientLogo,
          }}
          defaultSource={{ uri: defaultLogoUri }}
          style={styles.sideMenuProfileIcon}
          resizeMode={'contain'}
        />
        {clientId === 'devhereworks' && (
          <Text
            style={{
              position: 'absolute',
              left: 16,
              bottom: 6,
              fontFamily: 'Poppins_600SemiBold',
              color: Colors.light.hereworksBlack300,
            }}
          >
            DEV
          </Text>
        )}
      </View>
      <View style={styles.mainSideMenu}>
        <DrawerContentScrollView
          style={{
            padding: 0,
            margin: 0,
            height: 0,
            width: '100%',
          }}
          contentContainerStyle={{
            flex: 1,
          }}
          showsVerticalScrollIndicator={false}
          {...props}
        >
          <ExpandableDrawer
            navObj={props.navigation}
            initialState={props.state}
            collapsed={props.collapsed}
            isAdmin={isAdmin}
            permissions={permissions}
            enabledFeatures={enabledFeatures}
          />
        </DrawerContentScrollView>
      </View>
      <PressableStyled
        style={styles.collapseButton}
        onPress={() => props.setCollapsed()}
      >
        <DoubleRightIcon style={{ transform: [{ rotate: props.collapsed ? '0deg' : '180deg' }] }} />
        {props.collapsed ? <></> : <Text style={styles.collapseText}>Collapse sidebar</Text>}
      </PressableStyled>
      <Pressable
        style={(state) => [
          styles.profileSideMenu, state.hovered && Style.hovered, state.pressed && Style.pressed,
        ]}
        onPress={() => props.navigation.navigate('Profile')}
        testID='my-profile-section-btn'
      >
        <View
          style={{
            height: 48,
            width: 48,
            borderRadius: 24,
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: Colors.light.hereworksBlue100,
          }}
        >
          <ProfileIcon />
        </View>
        {props.collapsed ? <></> : <View style={{ flex: 1 }}>
          <Text style={styles.username} numberOfLines={1}>
            {`${firstName ?? ''} ${lastName ?? ''}`}
          </Text>
          <Text style={styles.viewProfile}>{'View profile'}</Text>
        </View>}
      </Pressable>
      <Pressable
        style={(state) => [{
          backgroundColor: supportOpen ? '#fff' : Colors.light.hereworksBlue500,
          borderWidth: 1,
          borderColor: Colors.light.hereworksBlue500,
          padding: 8,
          borderRadius: 20,
          position: 'absolute',
          marginLeft: window.width - 64,
          marginTop: window.height - 64,
          flexDirection: 'row',
        }, state.hovered && Style.hovered, state.pressed && Style.pressed]}
        onPress={() => {
          logEvent('CustomDrawer', 'support');
          setSupportOpen(!supportOpen);
        }}
        testID='ticket-support-form-btn'
      >
        {supportOpen ? <CloseModalIcon /> : <SupportIcon color={'#fff'} />}
      </Pressable>
      <Modal
        animationIn={'slideInRight'}
        animationOut={'slideOutRight'}
        isVisible={supportOpen}
        onBackdropPress={() => setSupportOpen(false)}
        style={{ margin: 0 }}
        backdropColor='transparent'>
        <View style={styles.supportContainer}>
          <SupportScreen
            navigation={useNavigation()}
            route={useRoute()} />
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  drawer: {
    height: 92, width: '100%', zIndex: 2, backgroundColor: '#fff',
  },
  sideMenuProfileIcon: {
    position: 'absolute', left: 20, width: 200, height: 90, backgroundColor: '#fff',
  },
  username: {
    fontFamily: 'Poppins_400Regular',
    fontSize: 16,
    marginLeft: 10,
    color: Colors.light.hereworksPrimaryGrey,
  },
  mainSideMenu: {
    backgroundColor: '#FFFFFF',
    flex: 1,
    shadowColor: '#fff',
    shadowRadius: 20,
    shadowOpacity: 0.1,
    shadowOffset: { width: 10, height: 0 },
    elevation: 5,
  },
  profileSideMenu: {
    paddingTop: 16,
    paddingLeft: 28,
    paddingBottom: 32,
    paddingRight: 16,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
  },
  viewProfile: {
    fontFamily: 'Roboto_400Regular',
    fontSize: 12,
    lineHeight: 14,
    marginLeft: 12,
    marginTop: 4,
    color: Colors.light.hereworksBlue400,
  },
  supportContainer: {
    backgroundColor: '#fff',
    position: 'absolute',
    bottom: 74,
    right: 24,
    margin: 0,
    borderRadius: 12,
    overflow: 'hidden',
    maxWidth: 420,
    shadowColor: '#000',
    shadowOpacity: 0.1,
    shadowRadius: 10,
  },
  collapseButton: {
    flexDirection: 'row',
    backgroundColor: '#fff',
    justifyContent: 'flex-start',
    alignItems: 'center',
    paddingVertical: 24,
    paddingLeft: 24,
    borderTopWidth: 1,
    borderColor: Colors.light.hereworksWhite500,
  },
  collapseText: {
    marginLeft: 10,
    fontFamily: 'Roboto_400Regular',
    fontSize: 14,
    color: Colors.light.hereworksBlack600,
  },
});

export default CustomDrawer;
